import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { BrandDisplayPeriodControl } from "@/components/brand-display-period-control";
import { TopCitedDomainsChart } from "@/components/dashboard/charts/top-cited-domains-chart";
import { TopCitedPagesCards } from "@/components/dashboard/charts/top-cited-pages-card";
import { BrandLlmVisibilityHeatmap } from "@/components/dashboard/charts/brand-llm-visibility-heatmap";
import { VisibilityScoreChart } from "@/components/dashboard/charts/visibility-score-chart";
import { LlmResponseCounters } from "@/components/dashboard/llm-response-counters";
import { MentionChart, RankingChart, SentimentChart } from "@/components/dashboard/metric-charts";
import { DashboardPageShell } from "@/components/patterns/headers";
import { getDisplayPeriodQuery } from "@/lib/display-period";
import { useBrandSearchActivity } from "@/hooks/use-brand-search-activity";
import { useDelayedHover } from "@/hooks/use-delayed-hover";
import { selectNewestSearchExecutionActivity, shouldInvalidateBrandChartsForActivity } from "@/lib/search-activity";
import { useTRPC } from "@/lib/trpc";
import { useUserPreferencesStore } from "@/state/store/userPreferences";

export const Route = createFileRoute("/_dashboard/$brand/dashboard")({
      component: Index,
});

export function Index() {
      const { brand } = Route.useParams();
      const trpc = useTRPC();
      const queryClient = useQueryClient();

      const defaultBrandDashboardPeriod = useUserPreferencesStore((state) => state.defaultBrandDashboardPeriod);
      const displayPeriodQuery = useMemo(
            () => getDisplayPeriodQuery(defaultBrandDashboardPeriod),
            [defaultBrandDashboardPeriod],
      );

      // Fetch brand first - this is the source of truth for brandId
      const { data: brandObject } = useQuery(trpc.brands.getBrandBySlug.queryOptions({ slug: brand }));
      const brandId = brandObject?.id;
      const { data: latestBrandExecutions } = useQuery(
            trpc.searchExecutions.getSearchExecutionsByBrandId.queryOptions(
                  {
                        brandId: brandId ?? "",
                        skip: 0,
                        take: 1,
                  },
                  { enabled: !!brandId },
            ),
      );

      const [showCompetitors, setShowCompetitors] = useState(true);
      const [hiddenCompetitorIds, setHiddenCompetitorIds] = useState<Set<string>>(new Set());
      const {
            activeValue: hoveredCompetitorId,
            clearHover: clearHoveredCompetitor,
            endHover: endHoveredCompetitor,
            startHover: startHoveredCompetitor,
      } = useDelayedHover<string>(0);

      const handleShowCompetitorsChange = (show: boolean) => {
            if (!show) {
                  clearHoveredCompetitor();
            }

            setShowCompetitors(show);
      };

      const handleToggleCompetitorVisibility = (brandId: string) => {
            setHiddenCompetitorIds((prev) => {
                  const next = new Set(prev);
                  const willHide = !next.has(brandId);

                  if (willHide && hoveredCompetitorId === brandId) {
                        clearHoveredCompetitor();
                  }

                  if (willHide) {
                        next.add(brandId);
                  } else {
                        next.delete(brandId);
                  }

                  return next;
            });
      };

      const { activity, latestEvent } = useBrandSearchActivity(brandId, { enabled: !!brandId });
      const latestBrandExecution = latestBrandExecutions?.items[0] ?? null;
      const dashboardLoadingActivity = selectNewestSearchExecutionActivity(latestBrandExecution, latestEvent, brandId);

      useEffect(() => {
            if (!latestEvent || !brandId) {
                  return;
            }

            const brandActivity = activity ?? {
                  ...latestEvent,
                  activeExecutionCount: latestEvent.isActive ? 1 : 0,
            };

            if (!shouldInvalidateBrandChartsForActivity(brandActivity)) {
                  return;
            }

            queryClient.invalidateQueries({
                  queryKey: trpc.brands.getBrandBySlug.queryKey({ slug: brand }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.brands.getBrandPromptsCount.queryKey({ brandId }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.brands.getBrandSearchExecutionsCount.queryKey({
                        brandId,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.brands.getBrandSearchSpecificationsCount.queryKey({
                        brandId,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.brands.getBrandPromptResponsesCount.queryKey({
                        brandId,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.promptResponses.getBrandPromptResponses.queryKey({
                        brandId,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.llms.getBrandPastSearchExecutionsLlms.queryKey({
                        brandId,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.charts.getDashboardMainChartData.queryKey({
                        brandId,
                        ...displayPeriodQuery,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.charts.getBrandRollupMetrics.queryKey({
                        brandId,
                        ...displayPeriodQuery,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.charts.getMentionChartData.queryKey({
                        brandId,
                        ...displayPeriodQuery,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.charts.getBrandLlmVisibilityHeatmapData.queryKey({
                        brandId,
                        ...displayPeriodQuery,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.charts.getSentimentChartData.queryKey({
                        brandId,
                        ...displayPeriodQuery,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.charts.getRankingChartData.queryKey({
                        brandId,
                        ...displayPeriodQuery,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.charts.getMostCitedDomainsChartData.queryKey({
                        brandId,
                        ...displayPeriodQuery,
                        limit: 10,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.charts.getTopCitedPagesData.queryKey({
                        brandId,
                        ...displayPeriodQuery,
                        limit: 10,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.competitors.getCompetitorsWithMetrics.queryKey({
                        parentBrandId: brandId,
                        ...displayPeriodQuery,
                  }),
            });
            queryClient.invalidateQueries({
                  queryKey: trpc.charts.getBrandLlmResponseCountsData.queryKey({
                        brandId,
                        ...displayPeriodQuery,
                  }),
            });
      }, [
            activity,
            brand,
            brandId,
            defaultBrandDashboardPeriod,
            displayPeriodQuery,
            latestEvent,
            queryClient,
            trpc.brands.getBrandBySlug,
            trpc.brands.getBrandPromptResponsesCount,
            trpc.brands.getBrandPromptsCount,
            trpc.brands.getBrandSearchExecutionsCount,
            trpc.brands.getBrandSearchSpecificationsCount,
            trpc.charts.getBrandRollupMetrics,
            trpc.charts.getBrandLlmResponseCountsData,
            trpc.charts.getDashboardMainChartData,
            trpc.charts.getBrandLlmVisibilityHeatmapData,
            trpc.charts.getMentionChartData,
            trpc.charts.getMostCitedDomainsChartData,
            trpc.charts.getRankingChartData,
            trpc.charts.getSentimentChartData,
            trpc.charts.getTopCitedPagesData,
            trpc.competitors.getCompetitorsWithMetrics,
            trpc.llms.getBrandPastSearchExecutionsLlms,
            trpc.promptResponses.getBrandPromptResponses,
      ]);

      return (
            <DashboardPageShell as="div" className="">
                  <div className="flex flex-col rounded-md ">
                        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                              {brandId && <LlmResponseCounters brandId={brandId} period={defaultBrandDashboardPeriod} />}
                              <BrandDisplayPeriodControl className="ml-auto xl:shrink-0" />
                        </div>
                        {/* Full-width Visibility Score Chart */}
                        {brandId && (
                              <div className="mt-4">
                                    <VisibilityScoreChart
                                          brandId={brandId}
                                          period={defaultBrandDashboardPeriod}
                                          activity={dashboardLoadingActivity}
                                          highlightedCompetitorId={hoveredCompetitorId}
                                          showCompetitors={showCompetitors}
                                          hiddenCompetitorIds={hiddenCompetitorIds}
                                          onShowCompetitorsChange={handleShowCompetitorsChange}
                                          onToggleCompetitorVisibility={handleToggleCompetitorVisibility}
                                          onCompetitorHoverStart={startHoveredCompetitor}
                                          onCompetitorHoverEnd={endHoveredCompetitor}
                                    />
                              </div>
                        )}

                        {/* Compact metric charts row */}
                        {brandId && (
                              <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                                    <MentionChart
                                          brandId={brandId}
                                          period={defaultBrandDashboardPeriod}
                                          activity={dashboardLoadingActivity}
                                          compact
                                          highlightedCompetitorId={hoveredCompetitorId}
                                          showCompetitors={showCompetitors}
                                          hiddenCompetitorIds={hiddenCompetitorIds}
                                    />
                                    <SentimentChart
                                          brandId={brandId}
                                          period={defaultBrandDashboardPeriod}
                                          activity={dashboardLoadingActivity}
                                          compact
                                          highlightedCompetitorId={hoveredCompetitorId}
                                          showCompetitors={showCompetitors}
                                          hiddenCompetitorIds={hiddenCompetitorIds}
                                    />
                                    <RankingChart
                                          brandId={brandId}
                                          period={defaultBrandDashboardPeriod}
                                          activity={dashboardLoadingActivity}
                                          compact
                                          highlightedCompetitorId={hoveredCompetitorId}
                                          showCompetitors={showCompetitors}
                                          hiddenCompetitorIds={hiddenCompetitorIds}
                                    />
                              </div>
                        )}

                        {brandId && (
                              <div className="mt-4">
                                    <BrandLlmVisibilityHeatmap
                                          brandId={brandId}
                                          period={defaultBrandDashboardPeriod}
                                          activity={dashboardLoadingActivity}
                                    />
                              </div>
                        )}

                        {brandId && (
                              <div className="mt-4">
                                    <TopCitedDomainsChart
                                          brandId={brandId}
                                          period={defaultBrandDashboardPeriod}
                                          activity={dashboardLoadingActivity}
                                    />
                              </div>
                        )}

                        {brandId && (
                              <div className="mt-4">
                                    <TopCitedPagesCards
                                          brandId={brandId}
                                          period={defaultBrandDashboardPeriod}
                                          activity={dashboardLoadingActivity}
                                    />
                              </div>
                        )}
                  </div>
            </DashboardPageShell>
      );
}
